if (!customElements.get('animated-product-slider')) {
  customElements.define('animated-product-slider', class extends HTMLElement {
    constructor() {
      super();
      this.index = 0;
      this.currentFrame = -1;
      this.switchTimer = null;
      this.scrollRaf = null;
      this.cursorRaf = null;
      this.bottleRaf = null;
      this.mobileBottleRaf = null;
      this.mobileBottleSwapTimer = null;
      this.bottleFrameTime = 0;
      this.isBottleHovered = false;
      this.isTransitioning = false;
      this.isInView = false;
      this.mobileBottleTriggeredInView = false;
      this.suspendMobileScrollSnapUntil = window.location.hash ? Date.now() + 3000 : 0;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    connectedCallback() {
      this.slides = [...this.querySelectorAll('.aps__slide')];
      this.buttons = [...this.querySelectorAll('.aps__nav-button')];
      this.pin = this.closest('.aps__pin');
      this.animationMode = this.dataset.animationMode || 'hover_frames_scroll_slides';
      if (!this.slides.length) return;
      this.style.setProperty('--aps-progress', 1 / this.slides.length);

      this.buttons.forEach((button, index) => {
        button.addEventListener('click', () => this.goTo(index, { syncScroll: true }));
        button.addEventListener('keydown', (event) => this.onNavKeydown(event, index));
      });

      this.onScroll = this.handleScroll.bind(this);
      this.onAnchorNavigation = (event) => {
        const link = event.target.closest('a[href*="#"]');
        if (!link) return;

        const targetUrl = new URL(link.href, window.location.href);
        const isSamePage = targetUrl.origin === window.location.origin
          && targetUrl.pathname === window.location.pathname
          && targetUrl.search === window.location.search;
        if (isSamePage && targetUrl.hash && targetUrl.hash !== '#') {
          this.suspendMobileScrollSnapUntil = Date.now() + 3000;
        }
      };
      this.onSectionLoad = (event) => {
        if (event.target.contains(this)) this.setupActiveFrame();
      };
      this.onBlockSelect = (event) => {
        const selectedSlide = event.target.closest('.aps__slide');
        const selectedIndex = this.slides.indexOf(selectedSlide);
        if (selectedIndex >= 0) this.goTo(selectedIndex, { syncScroll: true });
      };
      this.onPointerMove = (event) => {
        if (event.pointerType && event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
        if (event.target.closest('.aps__nav')) {
          this.onPointerLeave();
          this.updateBottleHover(event, false);
          this.classList.add('is-nav-hover');
          this.classList.remove('is-pointer-active');
          return;
        }
        this.classList.remove('is-nav-hover');
        this.pointerPosition = { clientX: event.clientX, clientY: event.clientY };
        this.queueCursorUpdate();
        this.updateBottleHover(event);
      };
      this.onPointerLeave = () => {
        cancelAnimationFrame(this.cursorRaf);
        this.cursorRaf = null;
        this.pointerPosition = null;
        this.classList.remove('is-pointer-active');
        this.classList.remove('is-nav-hover');
        this.stopBottleHover();
      };
      this.onTouchStart = (event) => {
        this.touchGesture = null;
        this.suppressSwipeClickUntil = 0;
        if (!window.matchMedia('(max-width: 749px)').matches
          || this.slides.length < 2 || this.isTransitioning
          || event.touches.length !== 1 || event.target.closest('.aps__nav')) return;
        const touch = event.touches[0];
        this.touchGesture = { x: touch.clientX, y: touch.clientY, axis: null };
      };
      this.onTouchMove = (event) => {
        const gesture = this.touchGesture;
        if (!gesture) return;
        if (event.touches.length !== 1) {
          this.touchGesture = null;
          return;
        }
        const dx = event.touches[0].clientX - gesture.x;
        const dy = event.touches[0].clientY - gesture.y;
        if (!gesture.axis && Math.max(Math.abs(dx), Math.abs(dy)) > 10) {
          gesture.axis = Math.abs(dx) > Math.abs(dy) * 1.25 ? 'horizontal' : 'vertical';
        }
        if (gesture.axis === 'horizontal' && event.cancelable) event.preventDefault();
      };
      this.onTouchEnd = (event) => {
        const gesture = this.touchGesture;
        this.touchGesture = null;
        if (!gesture || gesture.axis !== 'horizontal' || !event.changedTouches.length) return;
        const dx = event.changedTouches[0].clientX - gesture.x;
        // A horizontal drag must not open the product link underneath it.
        this.suppressSwipeClickUntil = Date.now() + 500;
        if (event.cancelable) event.preventDefault();
        if (Math.abs(dx) < 45) return;
        this.goTo(this.index + (dx < 0 ? 1 : -1), { syncScroll: true });
      };
      this.onTouchCancel = () => { this.touchGesture = null; };
      this.onSwipeClick = (event) => {
        if (event.detail > 0 && Date.now() < this.suppressSwipeClickUntil) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      };
      this.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.addEventListener('touchmove', this.onTouchMove, { passive: false });
      this.addEventListener('touchend', this.onTouchEnd, { passive: false });
      this.addEventListener('touchcancel', this.onTouchCancel);
      this.addEventListener('click', this.onSwipeClick, true);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      document.addEventListener('click', this.onAnchorNavigation, true);
      document.addEventListener('shopify:section:load', this.onSectionLoad);
      this.addEventListener('shopify:block:select', this.onBlockSelect);
      this.addEventListener('pointermove', this.onPointerMove, { passive: true });
      this.addEventListener('pointerleave', this.onPointerLeave);
      this.viewportObserver = new IntersectionObserver((entries) => {
        const isInView = entries[0]?.isIntersecting === true;
        const enteredView = isInView && !this.isInView;
        this.isInView = isInView;
        if (enteredView) {
          this.setupActiveFrame();
          this.checkMobileBottleTopTrigger();
        } else if (!isInView) {
          cancelAnimationFrame(this.mobileBottleRaf);
          this.mobileBottleRaf = null;
          clearTimeout(this.mobileBottleSwapTimer);
          this.mobileBottleSwapTimer = null;
          this.slides.forEach((slide) => slide.classList.remove('is-mobile-rotating'));
          this.mobileBottleTriggeredInView = false;
        }
      }, { threshold: 0.25 });
      this.viewportObserver.observe(this);
      this.applyTheme(this.slides[0]);
      this.setupActiveFrame();
      this.updateScrollProgress();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      document.removeEventListener('click', this.onAnchorNavigation, true);
      document.removeEventListener('shopify:section:load', this.onSectionLoad);
      this.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.removeEventListener('pointermove', this.onPointerMove);
      this.removeEventListener('pointerleave', this.onPointerLeave);
      this.removeEventListener('touchstart', this.onTouchStart);
      this.removeEventListener('touchmove', this.onTouchMove);
      this.removeEventListener('touchend', this.onTouchEnd);
      this.removeEventListener('touchcancel', this.onTouchCancel);
      this.removeEventListener('click', this.onSwipeClick, true);
      this.touchGesture = null;
      this.viewportObserver?.disconnect();
      clearTimeout(this.switchTimer);
      cancelAnimationFrame(this.scrollRaf);
      cancelAnimationFrame(this.cursorRaf);
      cancelAnimationFrame(this.bottleRaf);
      cancelAnimationFrame(this.mobileBottleRaf);
      clearTimeout(this.mobileBottleSwapTimer);
    }

    queueCursorUpdate() {
      if (this.cursorRaf !== null || !this.pointerPosition) return;
      this.cursorRaf = requestAnimationFrame(() => {
        this.cursorRaf = null;
        const cursor = this.slides[this.index]?.querySelector('.aps__discover');
        if (!cursor?.offsetParent || !this.pointerPosition) return;
        // Use the positioned stage, avoiding fixed descendants of a sticky layer in Safari.
        const bounds = cursor.offsetParent.getBoundingClientRect();
        cursor.style.setProperty('--aps-cursor-x', `${this.pointerPosition.clientX - bounds.left}px`);
        cursor.style.setProperty('--aps-cursor-y', `${this.pointerPosition.clientY - bounds.top}px`);
        this.classList.add('is-pointer-active');
      });
    }

    goTo(nextIndex, { syncScroll = false } = {}) {
      if (nextIndex === this.index || nextIndex < 0 || nextIndex >= this.slides.length || this.isTransitioning) return;
      clearTimeout(this.switchTimer);
      this.slides.forEach((slide) => slide.classList.remove('is-leaving'));
      const previous = this.slides[this.index];
      const next = this.slides[nextIndex];
      const direction = nextIndex > this.index ? 1 : -1;
      const previousIndex = this.index;
      this.isTransitioning = true;
      this.style.setProperty('--aps-direction', direction);
      this.cancelBottleAnimation(true);

      previous.classList.remove('is-active');
      previous.setAttribute('aria-hidden', 'true');
      next.classList.add('is-active');
      next.removeAttribute('aria-hidden');
      this.buttons[previousIndex].setAttribute('aria-selected', 'false');
      this.buttons[previousIndex].tabIndex = -1;
      this.buttons[nextIndex].setAttribute('aria-selected', 'true');
      this.buttons[nextIndex].tabIndex = 0;
      this.style.setProperty('--aps-progress', (nextIndex + 1) / this.slides.length);
      this.classList.remove('is-pointer-active');

      this.index = nextIndex;
      this.currentFrame = -1;
      this.applyTheme(next);
      this.setupActiveFrame();
      if (this.isInView && this.isAtViewportTop()) this.startMobileBottleRotation(nextIndex);
      if (syncScroll) this.syncScrollToSlide(nextIndex);
      this.updateScrollProgress();
      const nav = this.buttons[nextIndex].parentElement;
      nav.scrollTo({
        left: this.buttons[nextIndex].offsetLeft - (nav.clientWidth - this.buttons[nextIndex].offsetWidth) / 2,
        behavior: this.reducedMotion ? 'auto' : 'smooth'
      });

      this.switchTimer = setTimeout(() => {
        this.isTransitioning = false;
        this.updateScrollProgress();
      }, this.reducedMotion ? 0 : 900);
    }

    applyTheme(slide) {
      this.style.setProperty('--aps-bg', slide.dataset.background || '#e9e2d7');
      this.style.setProperty('--aps-accent', slide.dataset.accent || '#125d6d');
      this.style.setProperty('--aps-ink', slide.dataset.ink || '#1c1c1a');
    }

    onNavKeydown(event, index) {
      let next = index;
      if (event.key === 'ArrowRight') next = (index + 1) % this.buttons.length;
      else if (event.key === 'ArrowLeft') next = (index - 1 + this.buttons.length) % this.buttons.length;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = this.buttons.length - 1;
      else return;
      event.preventDefault();
      this.buttons[next].focus();
      this.goTo(next, { syncScroll: true });
    }

    syncScrollToSlide(slideIndex) {
      if (this.animationMode !== 'hover_frames_scroll_slides' || !this.pin || this.slides.length < 2) return;

      const rect = this.pin.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
      const pinTop = window.scrollY + rect.top;
      const progress = (slideIndex + 0.5) / this.slides.length;
      let targetScroll;

      if (this.pin.dataset.sticky === 'true') {
        const stickyViewportHeight = Math.max(1, viewportHeight - headerHeight);
        const scrollableDistance = Math.max(1, rect.height - stickyViewportHeight);
        targetScroll = pinTop - headerHeight + (progress * scrollableDistance);
      } else {
        const travel = rect.height + viewportHeight;
        targetScroll = pinTop - viewportHeight + (progress * travel);
      }

      window.scrollTo({ top: Math.max(0, targetScroll), behavior: 'auto' });
    }

    setupActiveFrame() {
      const slide = this.slides[this.index];
      const frames = [...slide.querySelectorAll('.aps__sequence-frame')];
      if (!frames.length) return;
      if (window.matchMedia('(max-width: 749px)').matches
        && slide.querySelector('.aps__mobile-bottle-image')
        && slide.dataset.mobileAutoRotate !== 'true') {
        this.currentFrame = 0;
        return;
      }
      frames.forEach((frame) => { frame.loading = 'eager'; });
      frames.forEach((frame, index) => frame.classList.toggle('is-current', index === 0));
      this.currentFrame = 0;
    }

    startMobileBottleRotation(slideIndex) {
      cancelAnimationFrame(this.mobileBottleRaf);
      this.mobileBottleRaf = null;
      clearTimeout(this.mobileBottleSwapTimer);
      this.mobileBottleSwapTimer = null;

      const slide = this.slides[slideIndex];
      if (!window.matchMedia('(max-width: 749px)').matches
        || this.reducedMotion
        || slide?.dataset.mobileAutoRotate !== 'true') return;

      const frames = [...slide.querySelectorAll('.aps__sequence-frame')];
      if (frames.length < 2) return;

      frames.forEach((frame) => { frame.loading = 'eager'; });
      slide.classList.add('is-mobile-rotating');
      this.setCurrentFrame(0);
      const duration = 1200;
      let startTime = null;

      const animate = (time) => {
        if (this.index !== slideIndex) return;
        if (startTime === null) startTime = time;
        const progress = Math.min(1, (time - startTime) / duration);
        this.setCurrentFrame(Math.floor(progress * (frames.length - 1)));
        if (progress < 1) {
          this.mobileBottleRaf = requestAnimationFrame(animate);
        } else {
          this.setCurrentFrame(frames.length - 1);
          this.mobileBottleRaf = null;
          if (slide.dataset.mobileShowStillAfter === 'true') {
            this.mobileBottleSwapTimer = setTimeout(() => {
              slide.classList.remove('is-mobile-rotating');
              this.mobileBottleSwapTimer = null;
            }, 1000);
          }
        }
      };

      this.mobileBottleRaf = requestAnimationFrame(animate);
    }

    isAtViewportTop() {
      const rect = this.getBoundingClientRect();
      const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
      return rect.top <= headerHeight + 2 && rect.bottom > headerHeight;
    }

    checkMobileBottleTopTrigger() {
      if (!this.isInView || this.mobileBottleTriggeredInView || !this.isAtViewportTop()) return;
      this.mobileBottleTriggeredInView = true;
      this.setupActiveFrame();
      this.startMobileBottleRotation(this.index);
    }

    setCurrentFrame(nextFrame) {
      const slide = this.slides[this.index];
      const frames = [...slide.querySelectorAll('.aps__sequence-frame')];
      if (!frames.length) return;
      const frameIndex = Math.min(frames.length - 1, Math.max(0, nextFrame));
      frames.forEach((frame, index) => frame.classList.toggle('is-current', index === frameIndex));
      this.currentFrame = frameIndex;
    }

    updateBottleHover(event, allowHover = true) {
      if (this.animationMode !== 'hover_frames_scroll_slides') return;
      if (window.matchMedia('(max-width: 749px)').matches) {
        this.stopBottleHover();
        return;
      }
      if (this.slides[this.index]?.dataset.bottleAnimation === 'false') {
        this.stopBottleHover();
        return;
      }
      const bottle = this.slides[this.index]?.querySelector('.aps__bottle-wrap');
      if (!allowHover || !bottle || this.reducedMotion || this.isTransitioning) {
        this.stopBottleHover();
        return;
      }

      const bounds = bottle.getBoundingClientRect();
      const isInside = event.clientX >= bounds.left
        && event.clientX <= bounds.right
        && event.clientY >= bounds.top
        && event.clientY <= bounds.bottom;

      if (isInside) this.startBottleHover();
      else this.stopBottleHover();
    }

    startBottleHover() {
      const frames = this.slides[this.index]?.querySelectorAll('.aps__sequence-frame');
      if (this.isBottleHovered || !frames || frames.length < 2) return;
      this.isBottleHovered = true;
      this.bottleFrameTime = 0;
      cancelAnimationFrame(this.bottleRaf);
      this.bottleRaf = requestAnimationFrame((time) => this.animateBottleForward(time));
    }

    stopBottleHover() {
      if (!this.isBottleHovered) return;
      this.isBottleHovered = false;
      this.bottleFrameTime = 0;
      cancelAnimationFrame(this.bottleRaf);
      this.bottleRaf = requestAnimationFrame((time) => this.animateBottleBack(time));
    }

    cancelBottleAnimation(resetFrame = false) {
      this.isBottleHovered = false;
      this.bottleFrameTime = 0;
      cancelAnimationFrame(this.bottleRaf);
      cancelAnimationFrame(this.mobileBottleRaf);
      clearTimeout(this.mobileBottleSwapTimer);
      this.bottleRaf = null;
      this.mobileBottleRaf = null;
      this.mobileBottleSwapTimer = null;
      this.slides.forEach((slide) => slide.classList.remove('is-mobile-rotating'));
      if (resetFrame && this.currentFrame > 0) this.setCurrentFrame(0);
    }

    animateBottleForward(time) {
      if (!this.isBottleHovered) return;
      const frames = this.slides[this.index]?.querySelectorAll('.aps__sequence-frame');
      if (!frames?.length) return;
      if (this.currentFrame >= frames.length - 1) {
        this.bottleRaf = null;
        return;
      }

      if (!this.bottleFrameTime || time - this.bottleFrameTime >= 70) {
        this.setCurrentFrame(this.currentFrame + 1);
        this.bottleFrameTime = time;
      }
      this.bottleRaf = requestAnimationFrame((nextTime) => this.animateBottleForward(nextTime));
    }

    animateBottleBack(time) {
      if (this.isBottleHovered) return;
      if (this.currentFrame <= 0) {
        this.setCurrentFrame(0);
        this.bottleRaf = null;
        return;
      }

      if (!this.bottleFrameTime || time - this.bottleFrameTime >= 55) {
        this.setCurrentFrame(this.currentFrame - 1);
        this.bottleFrameTime = time;
      }
      this.bottleRaf = requestAnimationFrame((nextTime) => this.animateBottleBack(nextTime));
    }

    handleScroll() {
      this.queueCursorUpdate();
      if (this.scrollRaf) return;
      this.scrollRaf = requestAnimationFrame(() => {
        this.updateScrollProgress();
        this.checkMobileBottleTopTrigger();
        this.scrollRaf = null;
      });
    }

    updateScrollProgress() {
      if (!this.slides.length || !this.pin) return;

      const isSticky = this.pin.dataset.sticky === 'true';
      const rect = this.pin.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
      let progress;

      if (isSticky) {
        const stickyViewportHeight = Math.max(1, viewportHeight - headerHeight);
        const scrollableDistance = Math.max(1, rect.height - stickyViewportHeight);
        progress = Math.min(1, Math.max(0, (headerHeight - rect.top) / scrollableDistance));
      } else {
        const travel = rect.height + viewportHeight;
        progress = Math.min(1, Math.max(0, (viewportHeight - rect.top) / travel));
      }

      if (this.animationMode === 'scroll_frames') {
        if (window.matchMedia('(max-width: 749px)').matches && this.slides[this.index].querySelector('.aps__mobile-bottle-image')) return;
        const frames = [...this.slides[this.index].querySelectorAll('.aps__sequence-frame')];
        if (!frames.length) return;
        this.setCurrentFrame(Math.round(progress * (frames.length - 1)));
        return;
      }

      const nextIndex = Math.min(this.slides.length - 1, Math.floor(progress * this.slides.length));
      if (nextIndex !== this.index && !this.isTransitioning) {
        const isMobile = window.matchMedia('(max-width: 749px)').matches;
        const isAnchorNavigation = Date.now() < this.suspendMobileScrollSnapUntil;
        if (isMobile && !isAnchorNavigation) {
          const adjacentIndex = this.index + Math.sign(nextIndex - this.index);
          this.goTo(adjacentIndex, { syncScroll: true });
        } else {
          this.goTo(nextIndex);
        }
      }
    }
  });
}
